<script lang='ts'>

    /* IMPORTS --------------------------------------------------------------------------------- */

    // COMPONENTS
    import AuctionItemHeader from './components/AuctionItemHeader.svelte';
    import AuctionItemFooter from './components/AuctionItemFooter.svelte';
    import AuctionItemPreviewBanner from './components/AuctionItemPreviewBanner.svelte';
    import AuctionItemReservationForm from './components/AuctionItemReservationForm.svelte';

    // MODULES
    import ProductList from 'src/app/routes/products/modules/ProductList/ProductList.svelte';

    // CASSIOPEIA
    import {
        addMessage,
        AuctionItem,
        AuthenticationFlow,
        ForgotPassword,
        Heading,
        Login,
        Modal,
        Overlay,
        SESSION,
        closeModal,
    } from '@smatch/cassiopeia-web-components';

    // COMPONENTS
    import Registration from 'src/app/routes/authentication/pages/registration/Registration.svelte';

    // LAYOUT
    import LayoutFull from 'src/app/library/layouts/layout-full/LayoutFull.svelte';

    // STYLING
    import 'tailwindcss/tailwind.css';

    // PLUGINS
    import { setContext } from 'svelte';
    import { navigate } from 'svelte-navigator';

    /* EXPORTS --------------------------------------------------------------------------------- */

    export let key: string | undefined = undefined;

    /* INITIALIZE ------------------------------------------------------------------------------ */

    setContext('scope', 'products');

    /* INTERNALS -------------------------------------------------------------------------------- */

    let isOpen = true;

    const closeOverlay = (): void => {
        isOpen = false;
    };

    $: isPreview = $SESSION !== null && $SESSION.user._.state === 'anonymous';

    const onRegistration = (): void => {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        navigate('/onboarding', { replace: true });
    };

    const onLogin = (): void => {
        closeModal();
    };

    const onError = (statusCode: string): boolean => {
        if (statusCode === 'STATUS_404') {
            navigate('/auctions');
            addMessage({
                // eslint-disable-next-line max-len
                description: 'The deal you\'re trying to view is no longer available. For any questions, please contact your account manager.',
                heading: 'Deal Offline',
                type: 'info',
            });
            return true;
        }
        return false;
    };

</script>

<LayoutFull>

    <AuctionItem { key } let:auction { onError }>

        <!-- TODO: Fix slot types -->
        <!-- eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -->
        <AuctionItemPreviewBanner total = { auction.total.quantity.products } { isPreview } />
        <AuctionItemHeader { auction } { isPreview } />
        <AuctionItemFooter { auction } { isPreview } />

        {#if isPreview }

            <Modal hash = '#login'>
                <Heading size = 'md'>Login</Heading>
                <Login registrationUrl = '#registration' on:success = { onLogin }/>
            </Modal>

            <Modal hash = '#authentication'>
                <Heading size = 'md'>Registration</Heading>
                <AuthenticationFlow registrationUrl = '#registration' loginUrl = '#login'/>
            </Modal>

            <Modal hash = '#authentication-download'>
                <Heading size = 'md'>Registration</Heading>
                <div class = 's8-hint'>
                    Please register to download the deal!
                </div>
                <AuthenticationFlow registrationUrl = '#registration' loginUrl = '#login'/>
            </Modal>

            <Modal hash = '#registration'>
                <Heading size = 'md'>Registration</Heading>
                <Registration loginUrl = '#login' on:success = { onRegistration }/>
            </Modal>

            <Modal hash = '#forgot-password'>
                <ForgotPassword />
            </Modal>

        {/if}

        <!-- eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -->
        {#if auction.isReserved }
            <Overlay bind:isOpen width = "380px" >
                <AuctionItemReservationForm { auction } on:save = { closeOverlay } />
            </Overlay>
        {/if}

        <main>
            <ProductList auctionKey = { key } { isPreview } { auction } />
        </main>

    </AuctionItem>

</LayoutFull>

<style lang='postcss'>

    @keyframes wiggle {
        10%, 90% {
            transform: translate3d(-1px, 0, 0);
        }

        20%, 80% {
            transform: translate3d(2px, 0, 0);
        }

        30%, 50%, 70% {
            transform: translate3d(-4px, 0, 0);
        }

        40%, 60% {
            transform: translate3d(4px, 0, 0);
        }
    }

    div.s8-hint {
        @apply bg-warning-400 py-1 px-2 rounded-md text-center text-warning-800;
        animation-name: wiggle;
        animation-duration: 1s;
        animation-iteration-count: 1;
    }

</style>